import { useCallback, useEffect, useRef, useState } from 'react'

export interface ReportAutoSaveState {
  isSaving: boolean
  lastSavedAt: Date | null
  hasUnsavedChanges: boolean
  error: string | null
}

export interface UseReportAutoSaveOptions<T> {
  enabled: boolean
  debounceMs?: number
  isLocked?: boolean
  onSave: (data: T) => Promise<void>
}

export const useReportAutoSave = <T>(
  formData: T,
  options: UseReportAutoSaveOptions<T>
) => {
  const { enabled, debounceMs = 1500, isLocked = false, onSave } = options

  const [autoSaveState, setAutoSaveState] = useState<ReportAutoSaveState>({
    isSaving: false,
    lastSavedAt: null,
    hasUnsavedChanges: false,
    error: null,
  })

  const previousFormDataRef = useRef<T | null>(null)
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isInitialLoadRef = useRef(true)
  const wasEnabledRef = useRef(enabled)

  // Reset initial load state when enabled changes from false to true
  // This ensures we use current form values as baseline when auto-save becomes active
  useEffect(() => {
    if (enabled && !wasEnabledRef.current) {
      isInitialLoadRef.current = true
    }
    wasEnabledRef.current = enabled
  }, [enabled])

  const performSave = useCallback(
    async (data: T) => {
      if (!enabled || isLocked) return

      setAutoSaveState((prev) => ({ ...prev, isSaving: true, error: null }))

      try {
        await onSave(data)

        setAutoSaveState((prev) => ({
          ...prev,
          isSaving: false,
          lastSavedAt: new Date(),
          hasUnsavedChanges: false,
          error: null,
        }))
      } catch (error) {
        console.error('Auto-save failed:', error)
        setAutoSaveState((prev) => ({
          ...prev,
          isSaving: false,
          error: error instanceof Error ? error.message : 'Auto-save failed',
        }))
      }
    },
    [enabled, isLocked, onSave]
  )

  const debouncedSave = useCallback(
    (data: T) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }

      saveTimeoutRef.current = setTimeout(() => {
        performSave(data)
      }, debounceMs)
    },
    [performSave, debounceMs]
  )

  useEffect(() => {
    if (isInitialLoadRef.current) {
      previousFormDataRef.current = formData
      isInitialLoadRef.current = false
      return
    }

    if (!enabled || isLocked) {
      return
    }

    const hasChanged =
      previousFormDataRef.current &&
      JSON.stringify(formData) !== JSON.stringify(previousFormDataRef.current)

    if (hasChanged) {
      setAutoSaveState((prev) => ({ ...prev, hasUnsavedChanges: true }))
      debouncedSave(formData)
    }
    previousFormDataRef.current = formData
  }, [formData, enabled, isLocked, debouncedSave])

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  const saveNow = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    performSave(formData)
  }, [performSave, formData])

  const resetError = useCallback(() => {
    setAutoSaveState((prev) => ({ ...prev, error: null }))
  }, [])

  return {
    autoSaveState,
    saveNow,
    resetError,
  }
}
